/**
 * Invoice Query Hook
 *
 * React Query hook for fetching a canonical invoice by ID.
 *
 * ## Features
 * - Type-safe query with ApiResult handling
 * - Automatic caching and refetching
 * - Loading and error states
 *
 * @module invoice/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { invoiceService } from '../api/invoiceService';

export interface UseInvoiceQueryOptions {
  /** Invoice ID to fetch */
  invoiceId: number | null;

  /** Whether the query is enabled (default: true if invoiceId exists) */
  enabled?: boolean;
}

/**
 * Fetch invoice by ID
 *
 * Returns the full React Query result with a convenience `invoice` field.
 *
 * @example
 * ```tsx
 * // Simple usage - convenience field
 * const { invoice, isLoading, error } = useInvoiceQuery({
 *   invoiceId: 184,
 * });
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!invoice) return <div>Invoice not found</div>;
 *
 * return (
 *   <div>
 *     <h1>Invoice #{invoice.id}</h1>
 *     <p>Processing Fee: ${invoice.totals.processingFee.toFixed(2)}</p>
 *   </div>
 * );
 *
 * // Advanced usage - access React Query features
 * const { invoice, isFetching, refetch, dataUpdatedAt } = useInvoiceQuery({
 *   invoiceId: 184,
 * });
 * ```
 */
export function useInvoiceQuery({
  invoiceId,
  enabled = true,
}: UseInvoiceQueryOptions) {
  const query = useQuery({
    // Invoice detail uses a non-club-scoped key because invoice IDs are globally
    // unique. This guarantees a single cache entry regardless of whether clubId
    // is available (post-purchase flow vs. direct confirmation URL visit).
    queryKey: invoiceId
      ? queryKeys.invoices.detail(invoiceId)
      : queryKeys.disabled.invoices(),
    queryFn: async () => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }

      const result = await invoiceService.getInvoiceById(invoiceId);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch invoice');
      }

      return result.payload;
    },
    // The API only needs invoiceId in the path — no clubId required.
    enabled: enabled && invoiceId !== null,
    ...cachePresets.stable,
  });

  return {
    ...query,
    invoice: query.data ?? null,
  };
}
